import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Tables } from "@/products/shared/services/supabase/types";
import { useAlerts as useAlertsHook } from '@/products/emailAnalytics/hooks/use-alerts';
import { useAuth } from '@/products/shared/state/auth-context';
import {  getAlertStatusValues } from '@/products/emailAnalytics/utils/config';

type Alert = Tables<'alerts'>;
// Use a flexible type for counts, keys will be lowercase status values
type AlertCounts = Record<string, number>;

interface AlertContextType {
  alertCounts: AlertCounts;
  refreshAlertCounts: () => Promise<void>;
  updateStatus: (id: number, newStatus: string) => Promise<boolean>;
  updateStatusByEmailId: (emailId: string, newStatus: string) => Promise<boolean>;
  updateAssignee: (id: number, newAssignee: string) => Promise<boolean>;
}

// Generate default counts dynamically
const generateDefaultCounts = (): AlertCounts => {
  const defaults: AlertCounts = {};
  getAlertStatusValues().forEach(statusValue => {
    defaults[statusValue] = 0;
  });
  // Add a total count if needed, or calculate on the fly elsewhere
  // defaults.total = 0;
  return defaults;
};

const defaultAlertCounts = generateDefaultCounts();

const AlertContext = createContext<AlertContextType>({
  alertCounts: defaultAlertCounts,
  refreshAlertCounts: async () => {},
  updateStatus: () => Promise.resolve(false),
  updateStatusByEmailId: () => Promise.resolve(false),
  updateAssignee: () => Promise.resolve(false),
});

export const useAlertContext = () => useContext(AlertContext);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alertCounts, setAlertCounts] = useState<AlertCounts>(defaultAlertCounts);
  const { user, loading: authLoading } = useAuth();
  
  const { 
    fetchAlerts,
    alerts,
    updateAlertStatus,
    updateAlertStatusByEmailId,
    updateAlertAssignee
  } = useAlertsHook();

  const calculateCounts = useCallback((alertsToCount: Alert[]): AlertCounts => {
    // Start with fresh default counts (all zeros)
    const counts = generateDefaultCounts();
    const validStatusValues = getAlertStatusValues();

    alertsToCount.forEach((alert) => {
      const status = alert.status?.toLowerCase();
      // Only count if the status is one of the configured valid statuses
      if (status && validStatusValues.includes(status)) {
        counts[status]++;
      } else if (status) {
        // Optional: Log or handle statuses found in data but not in config
        console.warn(`[AlertContext] Alert ${alert.id} has status "${alert.status}" not found in configured options.`);
        // You could add an 'other' count if desired
        // counts.other = (counts.other || 0) + 1;
      }
    });

    // counts.total = alertsToCount.length; // Add total if needed
    return counts;
  }, []);

  // Only fetch alerts when auth state changes and user is logged in
  useEffect(() => {
    if (authLoading || !user) return;

    fetchAlerts().catch((err) => {
      console.warn('[AlertContext] Error fetching alerts:', err);
    });
  }, [authLoading, user, fetchAlerts]);

  // Update counts when alerts change
  useEffect(() => {
    setAlertCounts(calculateCounts(alerts));
  }, [alerts, calculateCounts]);

  const refreshAlertCounts = useCallback(async () => {
    if (!user) return;
    
    try {
      await fetchAlerts();
    } catch (error) {
      console.error('[AlertContext] Error refreshing alerts:', error);
    }
  }, [user, fetchAlerts]);

  const updateStatus = useCallback(async (id: number, newStatus: string): Promise<boolean> => {
    // Ensure we pass the lowercase status value if that's what the hook expects
    const success = await updateAlertStatus(id, newStatus.toLowerCase());
    if (success) {
      await refreshAlertCounts();
    }
    return success;
  }, [updateAlertStatus, refreshAlertCounts]);

  const updateStatusByEmailId = useCallback(async (emailId: string, newStatus: string): Promise<boolean> => {
    console.log('[AlertContext] Updating status by email:', emailId, newStatus);
    // Ensure we pass the lowercase status value if that's what the hook expects
    const success = await updateAlertStatusByEmailId(emailId, newStatus.toLowerCase());
    if (success) {
      await refreshAlertCounts();
    }
    return success;
  }, [updateAlertStatusByEmailId, refreshAlertCounts]);

  const updateAssignee = useCallback(async (id: number, newAssignee: string): Promise<boolean> => {
    const success = await updateAlertAssignee(id, newAssignee);
    return success;
  }, [updateAlertAssignee]);

  const value = {
    alertCounts,
    refreshAlertCounts,
    updateStatus,
    updateStatusByEmailId,
    updateAssignee
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};
